import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import EllipsisVerticalIcon from '@heroicons/react/24/outline/EllipsisVerticalIcon';
import ChevronLeftIcon from '@heroicons/react/24/outline/ChevronLeftIcon';
import ChevronRightIcon from '@heroicons/react/24/outline/ChevronRightIcon';
import ChevronDownIcon from '@heroicons/react/24/outline/ChevronDownIcon';
import InboxIcon from '@heroicons/react/24/outline/InboxIcon';
import { IOSSwitch } from '../IOSSwitch';
import { MobileActionSheet } from './mobile-action-sheet';
import {
  IMAGE_COLUMN_IDS,
  STATUS_COLUMN_IDS,
  getCellText,
  getPaymentStatusColor,
  getPaymentStatusLabel,
  getRowImage,
  getStatusChipColor,
  getStatusChipLabel,
  isSwitchOn,
} from '../table-cell-helpers';

const TITLE_PRIORITY = ['product_name', 'name', 'userName', 'full_name', 'title', 'username', 'invoice_number'];
const SUBTITLE_IDS = ['order_number', 'placed_at', 'created_at', 'createdAt'];
const HIDDEN_DETAIL_IDS = ['id', 'actions', 'switch', 'payment_status', ...STATUS_COLUMN_IDS, ...IMAGE_COLUMN_IDS];
const VISIBLE_DETAILS = 4;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

function scrollToTop() {
  document.getElementById('mobile-scroll-container')?.scrollTo({ top: 0, behavior: 'smooth' });
}

function getLayout(headCells = []) {
  const ids = headCells.map((cell) => cell.id);
  const titleId = TITLE_PRIORITY.find((id) => ids.includes(id));
  const subtitleIds = SUBTITLE_IDS.filter((id) => ids.includes(id) && id !== titleId);
  const imageId = IMAGE_COLUMN_IDS.find((id) => ids.includes(id));
  const statusIds = ids.filter((id) => STATUS_COLUMN_IDS.includes(id) || id === 'payment_status');
  const detailCells = headCells.filter(
    (cell) => !HIDDEN_DETAIL_IDS.includes(cell.id) && cell.id !== titleId && !subtitleIds.includes(cell.id)
  );

  return {
    titleId,
    subtitleIds,
    imageId,
    statusIds,
    detailCells,
    hasActions: ids.includes('actions'),
    hasSwitch: ids.includes('switch'),
  };
}

function hasValue(value) {
  return value !== undefined && value !== null && `${value}`.trim() !== '';
}

function DetailItem({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="p" noWrap>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {hasValue(value) ? value : '—'}
      </Typography>
    </Box>
  );
}

function MobileRowCard({
  row,
  layout,
  isSelected,
  selectable,
  isSubmittingSwitch,
  onOpen,
  onToggleSelect,
  onOpenActions,
  onToggleSwitch,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const title = layout.titleId ? getCellText(row, layout.titleId) : getCellText(row, 'order_number');
  const subtitle = layout.subtitleIds
    .map((id) => (id === 'order_number' ? `#${getCellText(row, id)}` : getCellText(row, id)))
    .filter(hasValue)
    .join(' • ');
  const details = layout.detailCells.map((cell) => ({
    id: cell.id,
    label: cell.label,
    value: getCellText(row, cell.id),
  }));
  const visibleDetails = expanded ? details : details.slice(0, VISIBLE_DETAILS);
  const hiddenCount = details.length - VISIBLE_DETAILS;

  return (
    <Card
      variant="outlined"
      onClick={onOpen}
      sx={{
        borderRadius: 3,
        cursor: 'pointer',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? 'action.selected' : 'background.paper',
        transition: 'transform 120ms ease, background-color 120ms ease',
        '&:active': { transform: 'scale(0.99)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
        {selectable && (
          <Checkbox
            size="small"
            checked={isSelected}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => onToggleSelect(event.target.checked)}
            sx={{ ml: -1, mr: -0.5 }}
          />
        )}
        {layout.imageId && (
          <Avatar variant="rounded" src={getRowImage(row, layout.imageId)} sx={{ width: 44, height: 44 }} />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, fontSize: 15 }}>
            {hasValue(title) ? title : `#${row.id}`}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap component="p">
              {subtitle}
            </Typography>
          )}
        </Box>
        {layout.hasSwitch && (
          <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
            {isSubmittingSwitch ? (
              <CircularProgress size={22} />
            ) : (
              <IOSSwitch checked={isSwitchOn(row)} onChange={onToggleSwitch} />
            )}
          </Box>
        )}
        {layout.hasActions && (
          <IconButton
            size="small"
            aria-label="Row actions"
            onClick={(event) => {
              event.stopPropagation();
              onOpenActions();
            }}
            sx={{ mr: -0.75 }}
          >
            <SvgIcon fontSize="small" sx={{ color: 'text.primary' }}>
              <EllipsisVerticalIcon />
            </SvgIcon>
          </IconButton>
        )}
      </Stack>

      {layout.statusIds.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ px: 1.5, pb: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
          {layout.statusIds.map((id) => (
            <Chip
              key={id}
              size="small"
              label={id === 'payment_status' ? getPaymentStatusLabel(row) : getStatusChipLabel(row, id)}
              color={id === 'payment_status' ? getPaymentStatusColor(row) : getStatusChipColor(row, id)}
              sx={{ fontWeight: 600, fontSize: 11 }}
            />
          ))}
        </Stack>
      )}

      {details.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 1.5,
            rowGap: 1,
            px: 1.5,
            pt: 1,
            pb: hiddenCount > 0 ? 0.5 : 1.5,
            borderTop: '1px dashed',
            borderColor: 'divider',
          }}
        >
          {visibleDetails.map((detail) => (
            <DetailItem key={detail.id} label={detail.label} value={detail.value} />
          ))}
        </Box>
      )}

      {hiddenCount > 0 && (
        <Button
          fullWidth
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          endIcon={
            <SvgIcon
              fontSize="small"
              sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
            >
              <ChevronDownIcon />
            </SvgIcon>
          }
          sx={{ borderRadius: 0, py: 0.75, color: 'text.secondary', fontWeight: 600 }}
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </Button>
      )}
    </Card>
  );
}

function MobilePager({ count, page, rowsPerPage, onPageChange, onRowsPerPageChange }) {
  if (!count) {
    return null;
  }

  const from = page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);
  const isLastPage = (page + 1) * rowsPerPage >= count;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        mt: 1.5,
        p: 1,
        pl: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mr: 'auto' }}>
        {`${from}–${to}`}
        <Typography component="span" variant="body2" color="text.secondary">
          {` of ${count}`}
        </Typography>
      </Typography>
      {onRowsPerPageChange && (
        <Select
          size="small"
          value={rowsPerPage}
          onChange={(event) => {
            onRowsPerPageChange(event);
            scrollToTop();
          }}
          sx={{ '& .MuiSelect-select': { py: 0.5 } }}
        >
          {ROWS_PER_PAGE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>{`${option} / page`}</MenuItem>
          ))}
        </Select>
      )}
      <IconButton
        aria-label="Previous page"
        disabled={page === 0}
        onClick={(event) => {
          onPageChange(event, page - 1);
          scrollToTop();
        }}
      >
        <SvgIcon fontSize="small"><ChevronLeftIcon /></SvgIcon>
      </IconButton>
      <IconButton
        aria-label="Next page"
        disabled={isLastPage}
        onClick={(event) => {
          onPageChange(event, page + 1);
          scrollToTop();
        }}
      >
        <SvgIcon fontSize="small"><ChevronRightIcon /></SvgIcon>
      </IconButton>
    </Stack>
  );
}

export function MobileDataList({
  count = 0,
  items = [],
  onPageChange = () => { },
  onRowsPerPageChange,
  onSelectOne,
  onSelect,
  onSelectAll,
  onDeselectOne,
  onDeselectAll,
  page = 0,
  rowsPerPage = 0,
  selected = [],
  headCells,
  popoverItems,
  isLoading,
  switchFunction,
  isSubmitting,
}) {
  const layout = React.useMemo(() => getLayout(headCells), [headCells]);
  const [actionsRow, setActionsRow] = React.useState(null);
  const selectable = Boolean(onSelectAll);
  const selectedAll = items.length > 0 && selected.length === items.length;
  const selectedSome = selected.length > 0 && selected.length < items.length;

  return (
    <Box>
      {actionsRow && (
        <MobileActionSheet
          open={Boolean(actionsRow)}
          onClose={() => setActionsRow(null)}
          title={actionsRow.title}
          actions={popoverItems}
        />
      )}

      {selectable && items.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mb: 1,
            px: 0.5,
            py: 0.25,
            borderRadius: 2,
            bgcolor: selected.length > 0 ? 'action.selected' : 'transparent',
          }}
        >
          <Checkbox
            size="small"
            checked={selectedAll}
            indeterminate={selectedSome}
            onChange={(event) => (event.target.checked ? onSelectAll?.() : onDeselectAll?.())}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selected.length > 0 ? `${selected.length} selected` : 'Select all'}
          </Typography>
          {selected.length > 0 && (
            <Button size="small" sx={{ ml: 'auto' }} onClick={() => onDeselectAll?.()}>
              Clear
            </Button>
          )}
        </Stack>
      )}

      {isLoading && items.length > 0 && <LinearProgress sx={{ mb: 1, height: 3 }} />}

      <Stack spacing={1.25}>
        {items.map((row, index) => {
          const isSelected = selected.includes(row.id);

          return (
            <MobileRowCard
              key={row.id ?? index}
              row={row}
              layout={layout}
              isSelected={isSelected}
              selectable={selectable}
              isSubmittingSwitch={isSubmitting && selected[0]?.id === row.id}
              onOpen={() => onSelect?.(row, true)}
              onToggleSelect={(checked) => (checked ? onSelectOne?.(row.id) : onDeselectOne?.(row.id))}
              onOpenActions={() => {
                onSelect?.(row, false);
                const title = layout.titleId ? getCellText(row, layout.titleId) : '';
                setActionsRow({ title: hasValue(title) ? title : 'Actions' });
              }}
              onToggleSwitch={() => switchFunction?.(row)}
            />
          );
        })}

        {items.length === 0 && isLoading &&
          Array.from({ length: 4 }, (_, index) => (
            <Card key={index} variant="outlined" sx={{ borderRadius: 3, p: 1.5 }}>
              <Skeleton width="60%" height={22} />
              <Skeleton width="40%" height={16} />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Skeleton variant="rounded" width={80} height={22} />
                <Skeleton variant="rounded" width={80} height={22} />
              </Stack>
              <Skeleton height={16} sx={{ mt: 1 }} />
              <Skeleton width="80%" height={16} />
            </Card>
          ))}
      </Stack>

      {items.length === 0 && !isLoading && (
        <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.secondary' }}>
          <SvgIcon sx={{ fontSize: 44, opacity: 0.6 }}>
            <InboxIcon />
          </SvgIcon>
          <Typography variant="subtitle1">No items</Typography>
        </Stack>
      )}

      <MobilePager
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  );
}
